import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Edit, // Import the Edit icon
} from "lucide-react";
import logger from '../../../../utils/logger';
import RecordingModal from "./RecordingModal";
import RenameRecordingModal from "./RenameRecordingModal"; // Import RenameRecordingModal

import { getMeetingRecordings } from "../../../../services/meetingServices";
import { getMeetingRooms } from "../../../../services/roomServices";

const Recordings = () => {
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [rooms, setRooms] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false); // State for rename modal
  const [recordingToRename, setRecordingToRename] = useState(null); // State for recording to rename
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecordings, setTotalRecordings] = useState(0);

  const getRooms = async () => {
    try {
      const response = await getMeetingRooms(1, 100); // Get first 100 rooms
      const roomsData = response.data || [];
      setRooms(roomsData);

      // Set first room as default if available
      if (roomsData.length > 0) {
        setSelectedRoomId(roomsData[0].roomId);
      }
    } catch (err) {
      logger("log", "Error fetching rooms:", err);
      setError("Error fetching rooms");
    }
  };

  const getRecordings = useCallback(async () => {
    if (!selectedRoomId) return;

    setLoading(true);
    setError("");
    try {
      const response = await getMeetingRecordings(
        selectedRoomId,
        currentPage,
        rowsPerPage
      );
      const rowRecording = response.data || [];
      const filterEmptyFile = rowRecording.filter((item) => item.file);
      logger("log", "filterEmptyFile", filterEmptyFile);
      setRecordings(filterEmptyFile);
      setTotalRecordings(filterEmptyFile.length);
      setTotalPages(response.data.pageInfo?.totalPages || 1);
    } catch (err) {
      logger("log", err);
      setError("Error fetching recordings");
      setRecordings([]);
      setTotalRecordings(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [selectedRoomId, currentPage, rowsPerPage]);

  useEffect(() => {
    getRooms();
  }, []);

  useEffect(() => {
    if (selectedRoomId) {
      setCurrentPage(1); // Reset to first page when room changes
    }
  }, [selectedRoomId]);

  useEffect(() => {
    if (selectedRoomId) {
      getRecordings();
    }
  }, [selectedRoomId, currentPage, rowsPerPage, getRecordings]);

  const handleRecordingClick = (recording) => {
    setSelectedRecording(recording);
    setShowModal(true);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const handleDownload = (recording) => {
    const link = document.createElement("a");
    link.href = recording.file.fileUrl;
    link.download = recording.title || "recording.mp4";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen text-white p-3 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 md:gap-0">
          <h1 className="text-xl md:text-3xl font-bold text-white">
            RECORDINGS
          </h1>
          <div className="flex items-center justify-between md:justify-end space-x-2 md:space-x-4">
            <div className="text-xs md:text-sm text-gray-300">
              Total: {totalRecordings} recordings
            </div>
            <button className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-gray-400 bg-gray-700/50 flex items-center justify-center hover:bg-gray-600/50 transition-colors">
              <HelpCircle className="h-4 w-4 md:h-5 md:w-5 text-gray-300" />
            </button>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="rounded-xl border border-gray-600 overflow-hidden primary-card-background">
          {/* Card Header */}
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-white mb-1">
                  View Recordings
                </h2>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-700">
            <div className="flex flex-col md:flex-row items-stretch md:items-end space-y-3 md:space-y-0 md:space-x-4">
              {/* Room Selection */}
              <div className="flex flex-col flex-1">
                <label className="text-gray-400 text-xs md:text-sm mb-1">
                  Select Room
                </label>
                <div className="relative">
                  <select
                    value={selectedRoomId}
                    onChange={(e) => setSelectedRoomId(e.target.value)}
                    className="w-full md:w-64 pl-3 md:pl-4 pr-8 md:pr-10 py-2.5 md:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-500 transition-colors"
                  >
                    <option value="" className="bg-gray-700 text-white">
                      Select a room
                    </option>
                    {rooms.map((room) => (
                      <option
                        key={room.id}
                        value={room.roomId}
                        className="bg-gray-700 text-white"
                      >
                        {room.roomName} ({room.roomId})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Refresh Button */}
              <button
                onClick={() => getRecordings()}
                className="text-white px-3 md:px-4 py-2.5 md:py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors hover:opacity-90 text-sm md:text-base bg-primaryPurple"
              >
                <Search className="w-4 h-4" />
                <span>Refresh Recordings</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-3 md:p-6">
            {loading && (
              <div className="flex items-center justify-center py-12 md:py-16">
                <p className="text-gray-400 text-base md:text-lg">
                  Loading recordings...
                </p>
              </div>
            )}

            {error && (
              <div className="text-red-400 text-center py-4 text-sm md:text-base">
                {error}
              </div>
            )}

            {!loading && !error && recordings.length === 0 && (
              <div className="flex items-center justify-center py-12 md:py-16">
                <p className="text-gray-400 text-base md:text-lg">
                  No recordings available
                </p>
              </div>
            )}

            {!loading && !error && recordings.length > 0 && (
              <div className="space-y-3 md:space-y-4">
                {/* Table Header - Desktop Only */}
                <div className="hidden md:grid grid-cols-4 gap-4 px-4 py-3 bg-gray-700/50 rounded-lg">
                  <div className="text-gray-300 font-medium text-sm">NAME</div>
                  <div className="text-gray-300 font-medium text-sm">
                    RECORDING DATE
                  </div>
                  <div className="text-gray-300 font-medium text-sm">Size</div>
                  <div className="text-gray-300 font-medium text-sm text-right">
                    ACTIONS
                  </div>
                </div>

                {/* Recordings List */}
                <div className="space-y-2 md:space-y-2">
                  {recordings.map((recording) => (
                    <div
                      key={recording.id}
                      onClick={() => handleRecordingClick(recording)}
                      className="md:grid md:grid-cols-4 md:gap-4 px-3 md:px-4 py-3 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors cursor-pointer"
                    >
                      {/* Mobile Card Layout */}
                      <div className="md:hidden space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium text-sm truncate">
                              {recording.name || "Untitled Recording"}
                            </div>
                            <div className="text-gray-400 text-xs mt-1">
                              {recording.createdAt
                                ? new Date(
                                    recording.createdAt
                                  ).toLocaleDateString()
                                : "N/A"}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(recording);
                              }}
                              className="p-2 hover:bg-gray-600 rounded-lg transition-colors"
                              title="Download"
                            >
                              <Download className="h-4 w-4 text-gray-400 hover:text-white" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRecordingToRename(recording);
                                setShowRenameModal(true);
                              }}
                              className="p-2 hover:bg-gray-600 rounded-lg transition-colors"
                              title="Rename"
                            >
                              <Edit className="h-4 w-4 text-gray-400 hover:text-white" />
                            </button>
                          </div>
                        </div>
                        <div className="text-gray-300 text-xs">
                          Size:{" "}
                          {recording.file.size
                            ? `${(recording.file.size / (1024 * 1024)).toFixed(
                                2
                              )} MB`
                            : "0 MB"}
                        </div>
                      </div>

                      {/* Desktop Grid Layout */}
                      <div className="hidden md:block text-white font-medium truncate">
                        {recording.name || "Untitled Recording"}
                      </div>
                      <div className="hidden md:block text-gray-300 text-sm">
                        {recording.createdAt
                          ? new Date(recording.createdAt).toLocaleDateString()
                          : "N/A"}
                      </div>
                      <div className="hidden md:block text-gray-300 text-sm">
                        {recording.file.size
                          ? `${(recording.file.size / (1024 * 1024)).toFixed(
                              2
                            )} MB`
                          : "0 MB"}
                      </div>
                      <div className="hidden md:flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(recording);
                          }}
                          className="p-2 hover:bg-gray-600 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="h-4 w-4 text-gray-400 hover:text-white" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRecordingToRename(recording);
                            setShowRenameModal(true);
                          }}
                          className="p-2 hover:bg-gray-600 rounded-lg transition-colors"
                          title="Rename"
                        >
                          <Edit className="h-4 w-4 text-gray-400 hover:text-white" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && !error && recordings.length > 0 && (
            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-700">
              <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-0">
                <div className="flex items-center space-x-2 text-xs md:text-sm text-white">
                  <span>Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) =>
                      handleRowsPerPageChange(Number(e.target.value))
                    }
                    className="bg-gray-700 border border-gray-600 rounded-md px-2 py-1 text-white text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-gray-500 transition-colors"
                  >
                    <option value={10} className="bg-gray-700 text-white">
                      10
                    </option>
                    <option value={25} className="bg-gray-700 text-white">
                      25
                    </option>
                    <option value={50} className="bg-gray-700 text-white">
                      50
                    </option>
                  </select>
                </div>
                <div className="flex items-center space-x-2 md:space-x-4">
                  <span className="text-xs md:text-sm text-white">
                    {currentPage} of {totalPages}
                  </span>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronsLeft className="h-4 w-4 text-white" />
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4 text-white" />
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-4 w-4 text-white" />
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 md:p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronsRight className="h-4 w-4 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-gray-400 text-xs md:text-sm space-y-1 px-2">
          <p>R-Link Email Support:</p>
          <a href="mailto:support@r-link.com" className="underline cursor-pointer hover:text-white transition-colors my-4">
            support@r-link.com
          </a>
          <p>Copyright © R-Link LLC 2025.</p>
        </div>
      </div>

      {/* Recording Modal */}
      <RecordingModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedRecording(null);
        }}
        recording={selectedRecording}
      />

      {/* Rename Recording Modal */}
      <RenameRecordingModal
        isOpen={showRenameModal}
        onClose={() => {
          setShowRenameModal(false);
          setRecordingToRename(null);
        }}
        recording={recordingToRename}
        onSave={(renamedRecordingId, newName) => {
          setRecordings((prevRecordings) =>
            prevRecordings.map((rec) =>
              rec.id === renamedRecordingId ? { ...rec, name: newName } : rec
            )
          );
          setRecordingToRename(null);
          setShowRenameModal(false);
        }}
      />
    </div>
  );
};

export default Recordings;
